import { ICommand } from '../../../core/commands/ICommand';
import { MoveNodesCommand } from '../../../core/commands/TransformCommands';
import { ProcessLayoutService, LayoutOptions } from '../services/ProcessLayoutService';
import { ProcessSwimlaneLayoutService } from '../services/ProcessSwimlaneLayoutService';
import { useDiagramStore } from '../../../stores/diagramStore';
import { UpdateEdgeCommand } from '../../../core/commands/edges/UpdateEdgeCommand';
import { getNodeMutationManager } from '../../../core/commands/mutations/NodeMutationManager';
import { anchorSideFromPoint, chooseAnchorPairForStrategy, type Rect } from '../services/flowchartAnchors';
import { allocateEdgeAnchors, type AnchorSide } from '../services/edgeAnchorAllocation';
import type { Node, Edge } from '../../../core/models/scene';

export interface ApplyAutoLayoutParams {
  options: LayoutOptions;
  targetNodeIds?: string[];
}

/**
 * Applies auto-layout to a flowchart diagram. Two paths:
 *
 *  - Swimlane (pool table present, full-diagram tidy): delegates to
 *    `ProcessSwimlaneLayoutService` which lays out shapes in canvas
 *    space, respects lane membership, and grows the pool to fit.
 *  - Flat (everything else, including selection-mode tidy on swimlane
 *    diagrams): runs ELK over the chosen subgraph, anchors the layout
 *    to the first selected node so the rest of the diagram stays put,
 *    and reallocates `sequence_flow` anchors via `allocateEdgeAnchors`.
 *
 * All state changes go through `MoveNodesCommand` / `UpdateEdgeCommand`
 * inside a single Yjs transaction so undo is one click.
 */
export class ApplyAutoLayoutCommand implements ICommand {
  id: string;
  name: string;
  description?: string;
  timestamp: number;

  private params: ApplyAutoLayoutParams;
  private layoutService: ProcessLayoutService;
  private swimlaneService: ProcessSwimlaneLayoutService;
  private moveCommand?: MoveNodesCommand;
  private edgeUpdateCommands: UpdateEdgeCommand[] = [];
  /**
   * Captured pool dimensions before the swimlane Tidy resized the pool.
   * Replayed on undo / redo so the pool growth participates in the
   * undo stack instead of leaking past it.
   */
  private poolBefore?: { id: string; updates: Partial<Node> };
  private poolAfter?: { id: string; updates: Partial<Node> };
  /**
   * Captured on the first `execute()` so subsequent `redo()` calls
   * replay the same work instead of running ELK again — re-running the
   * layout from a different starting state would drift across cycles.
   */
  private didExecute = false;

  constructor(params: ApplyAutoLayoutParams) {
    this.id = `apply-auto-layout-${Date.now()}`;
    this.name = 'Apply Auto Layout';
    this.description = `Apply ${params.options.strategy} layout to flowchart`;
    this.timestamp = Date.now();
    this.params = params;
    this.layoutService = new ProcessLayoutService();
    this.swimlaneService = new ProcessSwimlaneLayoutService(this.layoutService);
  }

  async execute(): Promise<void> {
    if (this.didExecute) {
      // Subsequent execute() (i.e. redo) just replays captured commands.
      // Re-running the layout would re-allocate fresh commands from a
      // different starting state, drifting the result across undo/redo
      // cycles and losing the original undo records.
      this.replayCaptured();
      return;
    }
    const store = useDiagramStore.getState();
    const nodes = store.nodes;
    const edges = store.edges;

    const targetIds = this.params.targetNodeIds ?? [];
    const targetSet = targetIds.length > 0 ? new Set(targetIds) : null;

    // Swimlane vs flat. Selection-mode tidy stays on the flat path —
    // re-banding the entire pool to lay out a subset would be surprising.
    if (targetSet === null && ProcessSwimlaneLayoutService.appliesTo(nodes)) {
      await this.executeSwimlaneTidy(nodes, edges);
    } else {
      await this.executeFlat(nodes, edges, targetSet);
    }
    this.didExecute = true;
  }

  private replayCaptured(): void {
    const mutationManager = getNodeMutationManager();
    mutationManager.runInTransaction(() => {
      this.moveCommand?.execute();
      if (this.poolAfter) {
        useDiagramStore.getState().updateNodes(
          [{ id: this.poolAfter.id, updates: this.poolAfter.updates }],
        );
      }
      for (const cmd of this.edgeUpdateCommands) {
        cmd.execute();
      }
    });
  }

  private async executeSwimlaneTidy(nodes: Node[], edges: Edge[]): Promise<void> {
    const result = await this.swimlaneService.layout(nodes, edges, this.params.options);

    const mutationManager = getNodeMutationManager();
    mutationManager.runInTransaction(() => {
      if (result.moves.length > 0) {
        this.moveCommand = new MoveNodesCommand(result.moves);
        this.moveCommand.execute();
      }

      if (result.poolUpdate) {
        const pool = nodes.find((n) => n.id === result.poolUpdate!.id);
        if (pool) {
          // Capture before/after so undo can revert and redo can re-apply
          // the pool growth alongside the rest of the transaction. Only
          // mirror keys that actually exist on the pool today — capturing
          // an absent property as `undefined` would let undo overwrite a
          // missing value with an explicit `undefined`, which the store
          // does not treat as "delete this property."
          const beforeUpdates: Partial<Node> = {};
          const poolRecord = pool as unknown as Record<string, unknown>;
          for (const key of Object.keys(result.poolUpdate.updates) as (keyof Node)[]) {
            if (key in poolRecord) {
              (beforeUpdates as Record<string, unknown>)[key as string] = poolRecord[key as string];
            }
          }
          this.poolBefore = { id: result.poolUpdate.id, updates: beforeUpdates };
          this.poolAfter = result.poolUpdate;
          useDiagramStore.getState().updateNodes(
            [{ id: result.poolUpdate.id, updates: result.poolUpdate.updates }],
          );
        }
      }

      this.edgeUpdateCommands = [];
      const edgeRoutes = result.edgeRoutes ?? new Map();
      if (result.edgeAllocations.size === 0 && edgeRoutes.size === 0) return;

      for (const edge of edges) {
        const allocation = result.edgeAllocations.get(edge.id);
        const routeUpdate = edgeRoutes.get(edge.id);
        if (!allocation && !routeUpdate) continue;
        if (edge.fromAnchorLocked === true && edge.toAnchorLocked === true) continue;

        const updates: Partial<Edge> = {};
        const endpointUpdate = routeUpdate ?? allocation;
        if (!endpointUpdate) continue;
        if (edge.fromAnchorLocked !== true) {
          if (edge.fromAnchor !== endpointUpdate.fromAnchor) updates.fromAnchor = endpointUpdate.fromAnchor;
          if (edge.fromOffset !== endpointUpdate.fromOffset) updates.fromOffset = endpointUpdate.fromOffset;
        }
        if (edge.toAnchorLocked !== true) {
          if (edge.toAnchor !== endpointUpdate.toAnchor) updates.toAnchor = endpointUpdate.toAnchor;
          if (edge.toOffset !== endpointUpdate.toOffset) updates.toOffset = endpointUpdate.toOffset;
        }
        const routingUpdate = routeUpdate?.attrs ?? pickFlowRoutingUpdate(edge);
        if (routingUpdate) updates.attrs = routingUpdate;
        if (Object.keys(updates).length === 0) continue;

        const cmd = new UpdateEdgeCommand(edge.id, updates);
        cmd.execute();
        this.edgeUpdateCommands.push(cmd);
      }
    });
  }

  private async executeFlat(
    nodes: Node[],
    edges: Edge[],
    targetSet: Set<string> | null,
  ): Promise<void> {
    const layoutResult = targetSet
      ? await this.layoutService.applyLayout(
          nodes.filter((n) => targetSet.has(n.id)),
          edges.filter((e) => targetSet.has(e.from) && targetSet.has(e.to)),
          this.params.options,
        )
      : await this.layoutService.applyLayout(nodes, edges, this.params.options);

    if (layoutResult.nodePositions.size === 0) return;

    // Anchor the layout to the first selected node so unaffected shapes
    // stay where the user put them. Without this the entire diagram
    // would jump to ELK's origin on every selection-mode tidy.
    const anchorId =
      (this.params.targetNodeIds && this.params.targetNodeIds.length > 0
        ? this.params.targetNodeIds[0]
        : undefined) ?? Array.from(layoutResult.nodePositions.keys())[0];
    const anchorBefore = anchorId ? nodes.find((n) => n.id === anchorId) : undefined;
    const anchorLayout = anchorId ? layoutResult.nodePositions.get(anchorId) : undefined;
    const dx = anchorBefore && anchorLayout ? (anchorBefore.x ?? 0) - anchorLayout.x : 0;
    const dy = anchorBefore && anchorLayout ? (anchorBefore.y ?? 0) - anchorLayout.y : 0;

    const shiftedEdgeEndpoints = new Map<string, { start: { x: number; y: number }; end: { x: number; y: number } }>();
    for (const [edgeId, pts] of layoutResult.edgeEndpoints.entries()) {
      shiftedEdgeEndpoints.set(edgeId, {
        start: { x: pts.start.x + dx, y: pts.start.y + dy },
        end: { x: pts.end.x + dx, y: pts.end.y + dy },
      });
    }

    const moves = Array.from(layoutResult.nodePositions.entries()).map(([nodeId, newPos]) => {
      const node = nodes.find((n) => n.id === nodeId);
      const oldPos = node ? { x: node.x ?? 0, y: node.y ?? 0 } : undefined;
      return {
        nodeId,
        oldPosition: oldPos,
        newPosition: { x: newPos.x + dx, y: newPos.y + dy },
      };
    });

    this.moveCommand = new MoveNodesCommand(moves);
    await this.moveCommand.execute();

    await waitForNextFrame();

    const movedPos = new Map(moves.map((m) => [m.nodeId, m.newPosition] as const));
    const storeAfter = useDiagramStore.getState();
    this.edgeUpdateCommands = [];

    const rectOf = (n: Node): Rect => {
      const w = n.width ?? n.w ?? 120;
      const h = n.height ?? n.h ?? 80;
      const pos = movedPos.get(n.id);
      const x = pos?.x ?? n.x ?? 0;
      const y = pos?.y ?? n.y ?? 0;
      return { x, y, w, h, cx: x + w / 2, cy: y + h / 2 };
    };

    // Build the candidate edge list (sequence_flow only, free anchors
    // skipped, target-respecting in selection mode). `allocateEdgeAnchors`
    // in 'redistribute' mode staggers parallel edges via (i+1)/(n+1)
    // instead of collapsing all of them onto 0.5.
    const candidateEdges: Array<{
      id: string;
      from: string;
      to: string;
      type?: string;
      fromAnchor?: AnchorSide;
      toAnchor?: AnchorSide;
      fromOffset?: number | null;
      toOffset?: number | null;
      fromLocked: boolean;
      toLocked: boolean;
    }> = [];
    for (const e of storeAfter.edges) {
      if ((e.type as string) !== 'sequence_flow') continue;
      if (!e.from || !e.to) continue;
      if (e.from.startsWith('__free') || e.to.startsWith('__free')) continue;
      if (targetSet && !targetSet.has(e.from) && !targetSet.has(e.to)) continue;
      candidateEdges.push({
        id: e.id,
        from: e.from,
        to: e.to,
        type: e.type as string,
        fromAnchor: e.fromAnchor as AnchorSide | undefined,
        toAnchor: e.toAnchor as AnchorSide | undefined,
        fromOffset: e.fromOffset ?? null,
        toOffset: e.toOffset ?? null,
        fromLocked: !!e.fromAnchorLocked,
        toLocked: !!e.toAnchorLocked,
      });
    }

    const allocations = allocateEdgeAnchors(
      candidateEdges,
      (edge) => {
        const fromNode = storeAfter.nodes.find((n) => n.id === edge.from);
        const toNode = storeAfter.nodes.find((n) => n.id === edge.to);
        if (!fromNode || !toNode) return null;
        // Prefer ELK-computed endpoints when available — sharpest side
        // pick because they reflect the actual routed entry/exit point.
        // Fall back to the strategy-based pair otherwise.
        const endpoints = shiftedEdgeEndpoints.get(edge.id);
        if (endpoints) {
          return {
            fromSide: anchorSideFromPoint(rectOf(fromNode), endpoints.start) as AnchorSide,
            toSide: anchorSideFromPoint(rectOf(toNode), endpoints.end) as AnchorSide,
          };
        }
        const pair = chooseAnchorPairForStrategy(rectOf(fromNode), rectOf(toNode), this.params.options.strategy);
        if (!pair.aSide || !pair.bSide) return null;
        return { fromSide: pair.aSide as AnchorSide, toSide: pair.bSide as AnchorSide };
      },
      { mode: 'redistribute' },
    );

    // Single Yjs transaction so we don't emit one Electric/E2E pending
    // sync per edge.
    const mutationManager = getNodeMutationManager();
    mutationManager.runInTransaction(() => {
      for (const edge of candidateEdges) {
        const allocation = allocations.get(edge.id);
        if (!allocation) continue;

        const updates: Partial<Edge> = {};
        if (!edge.fromLocked) {
          if (edge.fromAnchor !== allocation.fromAnchor) updates.fromAnchor = allocation.fromAnchor;
          if (edge.fromOffset !== allocation.fromOffset) updates.fromOffset = allocation.fromOffset;
        }
        if (!edge.toLocked) {
          if (edge.toAnchor !== allocation.toAnchor) updates.toAnchor = allocation.toAnchor;
          if (edge.toOffset !== allocation.toOffset) updates.toOffset = allocation.toOffset;
        }
        // Look up the live edge in the store so we can read existing
        // `attrs.routingType` (the candidateEdges projection drops it).
        const liveEdge = storeAfter.edges.find((e) => e.id === edge.id);
        const routingUpdate = pickFlowRoutingUpdate(liveEdge);
        if (routingUpdate) updates.attrs = routingUpdate;
        if (Object.keys(updates).length === 0) continue;

        const cmd = new UpdateEdgeCommand(edge.id, updates);
        cmd.execute();
        this.edgeUpdateCommands.push(cmd);
      }
    });
  }

  async undo(): Promise<void> {
    const mutationManager = getNodeMutationManager();
    mutationManager.runInTransaction(() => {
      // Best-effort undo: any single edge or the move command failing
      // shouldn't strand the rest of the transaction. Errors here are
      // already handled inside each command's own undo path; we just
      // don't want one stale handle to abort the whole rewind.
      for (let i = this.edgeUpdateCommands.length - 1; i >= 0; i -= 1) {
        try {
          this.edgeUpdateCommands[i]!.undo();
        } catch {
          /* swallow per-edge undo failures */
        }
      }
      if (this.poolBefore) {
        try {
          useDiagramStore.getState().updateNodes(
            [{ id: this.poolBefore.id, updates: this.poolBefore.updates }],
          );
        } catch {
          /* swallow pool revert failures */
        }
      }
      try {
        this.moveCommand?.undo();
      } catch {
        /* swallow MoveNodesCommand undo failures */
      }
    });
  }

  async redo(): Promise<void> {
    // Replays captured commands via the cached path in `execute()`.
    await this.execute();
  }

  canExecute(): boolean {
    return useDiagramStore.getState().nodes.length > 0;
  }

  canUndo(): boolean {
    // Edge-only and pool-only runs (e.g. swimlane Tidy with no node moves)
    // still need to be undoable.
    return (
      !!this.moveCommand ||
      this.edgeUpdateCommands.length > 0 ||
      !!this.poolBefore
    );
  }
}

function waitForNextFrame(): Promise<void> {
  return new Promise<void>((resolve) => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => resolve());
    } else {
      setTimeout(() => resolve(), 0);
    }
  });
}

const FLOW_EDGE_TYPES = new Set(['sequence_flow', 'message_flow', 'exception_flow']);

/**
 * Returns an `attrs` patch that promotes a flow edge's `routingType`
 * to `'orthogonal'` so the canvas renders right-angle paths around
 * shapes instead of bezier curves *through* them. Returns null when
 * - the edge isn't a flow type (e.g. a free-form line),
 * - the edge already has any routingType set (respect user choice),
 * - or the edge object isn't available.
 *
 * Used by both Tidy paths so a fresh layout produces the same
 * connector aesthetic the materializer now emits at import time.
 */
function pickFlowRoutingUpdate(
  edge: Edge | { type?: string; attrs?: Record<string, unknown> } | undefined,
): Record<string, unknown> | null {
  if (!edge) return null;
  if (!FLOW_EDGE_TYPES.has(edge.type as string)) return null;
  const existing = (edge.attrs as Record<string, unknown> | undefined)?.routingType;
  if (typeof existing === 'string' && existing.length > 0) return null;
  return { routingType: 'orthogonal' };
}
